import { tz } from '@date-fns/tz';
import { api } from '@packages/backend/convex/_generated/api';
import { convertToLocalFormat, formatCents } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { FunctionReturnType } from 'convex/server';
import { useQuery } from 'convex-helpers/react';
import { format } from 'date-fns';
import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { ActionButton } from '@/components/ActionButton';
import { CopyTextButton } from '@/components/CopyTextButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AiPhoneCallStatusBadge,
  formatCallLength,
  isCallLive,
  LiveDot,
  SentimentBadge,
  SuccessScoreBadge,
  useNow,
} from '@/features/ai-phone-calls/AiPhoneCallBadges';
import { SuperAdminOnly } from '@/features/auth/SuperAdminOnly';
import { OrderEventsCard } from '@/features/orders/OrderEventsCard';
import { PhoneCallTranscriptCard } from './PhoneCallTranscriptCard';
import { type CallConversation, useCallConversation } from './useCallConversation';

export const Route = createFileRoute('/r/$restaurantId/phone-calls/$callId')({
  component: PhoneCallDetailsPage,
});

type Call = NonNullable<FunctionReturnType<typeof api.aiPhone.getCallById>>;

function PhoneCallDetailsPage() {
  const { callId } = Route.useParams();
  const callQuery = useQuery(api.aiPhone.getCallById, { callId });
  if (callQuery.isPending) return <LoadingIcon />;
  if (callQuery.isError) {
    return <ErrorAlert title="Error loading the call" message={callQuery.error.message} />;
  }
  if (callQuery.data === null) return <div>Call not found</div>;
  return <PhoneCallDetails call={callQuery.data} />;
}

/**
 * One AI phone call: how it went, the cart as the agent builds it (live, from Convex), and the
 * transcript and recording (from ElevenLabs). Super admins also get the call's PostHog events.
 */
const PhoneCallDetails = ({ call }: { call: Call }) => {
  const now = useNow([call]);
  const isLive = isCallLive(call, now);
  const { conversation, errorMessage, retry } = useCallConversation(call.id);

  return (
    <div className="space-y-6">
      <PhoneCallSummaryCard call={call} conversation={conversation} isLive={isLive} now={now} />
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <PhoneCallCartCard call={call} isLive={isLive} />
        <PhoneCallTranscriptCard
          callId={call.id}
          conversation={conversation}
          errorMessage={errorMessage}
          isLive={isLive}
          onRetry={retry}
        />
      </div>
      <SuperAdminOnly>
        <OrderEventsCard
          orderCreatedAt={call.startedAt}
          orderUuid={call.orderUuid}
          restaurantTimeZone={call.restaurantTimeZone}
          title="Call events"
        />
      </SuperAdminOnly>
    </div>
  );
};

const PhoneCallSummaryCard = ({
  call,
  conversation,
  isLive,
  now,
}: {
  call: Call;
  conversation: CallConversation | null;
  isLive: boolean;
  now: number;
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <ActionButton asChild className="h-7 w-7" size="icon" variant="outline">
            <Link params={{ restaurantId: call.restaurantId }} to="/r/$restaurantId/phone-calls">
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Back to phone calls</span>
            </Link>
          </ActionButton>
          <CardTitle>
            {call.callerPhoneNumber
              ? `Call from ${convertToLocalFormat(call.callerPhoneNumber)}`
              : 'Call from a hidden number'}
          </CardTitle>
          <AiPhoneCallStatusBadge isLive={isLive} status={call.status} />
        </div>
        <CardDescription>
          {[
            format(call.startedAt, 'h:mm a, d MMM yyyy', { in: tz(call.restaurantTimeZone) }),
            formatCallLength(call, isLive, now),
            call.restaurantName,
            conversation?.title ?? null,
          ]
            .filter((part) => part !== null)
            .join(' · ')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <SummaryItem label="Success">
            <SuccessScoreBadge score={call.successScore} />
          </SummaryItem>
          <SummaryItem label="Mood">
            <SentimentBadge sentiment={call.sentiment} />
          </SummaryItem>
          <SummaryItem label="Order">
            {call.isOrderPlaced ? (
              <Link
                className="font-medium hover:underline"
                params={{ orderUuid: call.orderUuid, restaurantId: call.restaurantId }}
                to="/r/$restaurantId/orders/$orderUuid"
              >
                {call.order?.localOrderId ? `#${call.order.localOrderId}` : 'View order'}
              </Link>
            ) : (
              <span className="text-muted-foreground">{isLive ? 'Not placed yet' : 'None'}</span>
            )}
          </SummaryItem>
          <SummaryItem label="Ended">
            <span className="text-sm">{conversation?.endedReason ?? '-'}</span>
          </SummaryItem>
        </dl>
        {call.summary && <p className="text-sm">{call.summary}</p>}
        {conversation?.successScoreRationale && (
          <p className="text-muted-foreground text-sm">
            {`Why this score: ${conversation.successScoreRationale}`}
          </p>
        )}
        <SuperAdminOnly>
          <p className="flex items-center gap-1 text-muted-foreground text-xs">
            {`ElevenLabs conversation ${call.conversationId}`}
            <CopyTextButton text={call.conversationId} />
          </p>
        </SuperAdminOnly>
      </CardContent>
    </Card>
  );
};

const SummaryItem = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-1">
    <dt className="text-muted-foreground text-xs">{label}</dt>
    <dd>{children}</dd>
  </div>
);

/** The caller's cart, priced from the live menu, as the agent builds it. */
const PhoneCallCartCard = ({ call, isLive }: { call: Call; isLive: boolean }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        {isLive && <LiveDot />}
        Order
      </CardTitle>
      {isLive && <CardDescription>Updates as the agent takes the order</CardDescription>}
    </CardHeader>
    <CardContent className="space-y-4">
      {call.cart.lines.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {isLive ? 'Nothing in the cart yet' : 'Nothing was added to the cart'}
        </p>
      ) : (
        <ul className="divide-y">
          {call.cart.lines.map((line) => (
            <li className="flex justify-between gap-4 py-2" key={line.lineId}>
              <div className="space-y-0.5">
                <div className="font-medium">{`${line.quantity} × ${line.name}`}</div>
                {line.options.length > 0 && (
                  <div className="text-muted-foreground text-sm">{line.options.join(', ')}</div>
                )}
                {line.notes && <div className="text-sm italic">{`"${line.notes}"`}</div>}
                {line.problem && (
                  <div className="text-red-600 text-sm dark:text-red-400">{line.problem}</div>
                )}
              </div>
              <div className="tabular-nums">{line.price}</div>
            </li>
          ))}
          <li className="flex justify-between py-2 font-semibold">
            <span>Total</span>
            <span className="tabular-nums">{call.cart.total}</span>
          </li>
        </ul>
      )}
      {call.order && (
        <p className="text-sm">
          {`Placed as ${call.order.localOrderId ? `order #${call.order.localOrderId}` : 'an order'} for ${call.order.customerName ?? 'the caller'}, ${formatCents(call.order.total, '$')}. `}
          <Link
            className="font-medium underline"
            params={{ orderUuid: call.orderUuid, restaurantId: call.restaurantId }}
            to="/r/$restaurantId/orders/$orderUuid"
          >
            View order
          </Link>
        </p>
      )}
      {!call.isOrderPlaced && !isLive && call.cart.lines.length > 0 && (
        <p className="text-muted-foreground text-sm">The caller didn't place this order.</p>
      )}
    </CardContent>
  </Card>
);
